import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Modal, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { CommonLayout } from 'common/components/Layouts/CommonLayout';
import { ELayoutSize } from 'common/const/common.const';
import { formRules } from 'common/const/common.const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  addGroup,
  addRegion,
  deleteGroup,
  deleteRegion,
  getGroupCollection,
  getRegionCollection,
  updateGroup,
  updateRegion,
} from 'app/store/reducers/region.reducer';
import { IGroupModel, IRegionModel } from 'entities/Region/Region.models';

type TRegionModalMode = { type: 'create' } | { type: 'edit'; region: IRegionModel } | null;
type TGroupModalMode =
  | { type: 'create'; regionId: string }
  | { type: 'edit'; group: IGroupModel }
  | null;

export const RegionsGroupsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { regionCollection, regionCollectionLoading, groupCollection, groupCollectionLoading } = useAppSelector(
    (state) => state.region
  );

  const [regionModal, setRegionModal] = useState<TRegionModalMode>(null);
  const [groupModal, setGroupModal] = useState<TGroupModalMode>(null);
  const [regionForm] = Form.useForm();
  const [groupForm] = Form.useForm();

  useEffect(() => {
    dispatch(getRegionCollection());
    dispatch(getGroupCollection());
  }, [dispatch]);

  const groupsByRegion = (regionId: string): IGroupModel[] =>
    (groupCollection?.data || []).filter((group) => group.regionId === regionId);

  // region modal

  const openRegionCreate = () => {
    regionForm.resetFields();
    setRegionModal({ type: 'create' });
  };

  const openRegionEdit = (region: IRegionModel) => {
    regionForm.setFieldsValue({ name: region.name });
    setRegionModal({ type: 'edit', region });
  };

  const submitRegion = () => {
    regionForm.validateFields().then(({ name }) => {
      const onSuccess = () => setRegionModal(null);

      if (regionModal?.type === 'edit') {
        dispatch(updateRegion({ id: regionModal.region.id, name }, onSuccess));
      } else {
        dispatch(addRegion({ name }, onSuccess));
      }
    });
  };

  const confirmDeleteRegion = (region: IRegionModel) => {
    Modal.confirm({
      title: `Delete region "${region.name}"?`,
      content: 'Regions referenced by groups or stores cannot be deleted.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => dispatch(deleteRegion(region.id)),
    });
  };

  // group modal

  const openGroupCreate = (regionId: string) => {
    groupForm.resetFields();
    setGroupModal({ type: 'create', regionId });
  };

  const openGroupEdit = (group: IGroupModel) => {
    groupForm.setFieldsValue({ name: group.name });
    setGroupModal({ type: 'edit', group });
  };

  const submitGroup = () => {
    groupForm.validateFields().then(({ name }) => {
      const onSuccess = () => setGroupModal(null);

      if (groupModal?.type === 'edit') {
        dispatch(updateGroup({ id: groupModal.group.id, name }, onSuccess));
      } else if (groupModal?.type === 'create') {
        // A group always requires a parent region — guaranteed by entering this branch from a region row.
        dispatch(addGroup({ name, regionId: groupModal.regionId }, onSuccess));
      }
    });
  };

  const confirmDeleteGroup = (group: IGroupModel) => {
    Modal.confirm({
      title: `Delete group "${group.name}"?`,
      content: 'Groups referenced by stores cannot be deleted.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => dispatch(deleteGroup(group.id)),
    });
  };

  const regionColumns: ColumnsType<IRegionModel> = [
    {
      title: 'Region',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      render: (_: unknown, region: IRegionModel) => (
        <Space>
          <Button size="small" type="primary" onClick={() => openGroupCreate(region.id)}>
            Add group
          </Button>
          <Button size="small" onClick={() => openRegionEdit(region)}>
            Edit
          </Button>
          <Button size="small" danger onClick={() => confirmDeleteRegion(region)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const groupColumns: ColumnsType<IGroupModel> = [
    {
      title: 'Group',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      render: (_: unknown, group: IGroupModel) => (
        <Space>
          <Button size="small" onClick={() => openGroupEdit(group)}>
            Edit
          </Button>
          <Button size="small" danger onClick={() => confirmDeleteGroup(group)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <CommonLayout typeLayout={ELayoutSize.Large} classStyle="mt-10">
      <Row className="mb-10 width-full" justify="space-between" align="middle">
        <Col>
          <span className="basic__text_title" title="Regions & Groups">
            Regions &amp; Groups
          </span>
        </Col>
        <Col>
          <Button type="primary" onClick={openRegionCreate}>
            Add new region
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="id"
        loading={regionCollectionLoading || groupCollectionLoading}
        columns={regionColumns}
        dataSource={regionCollection?.data || []}
        pagination={false}
        expandable={{
          expandedRowRender: (region) => (
            <Table
              rowKey="id"
              columns={groupColumns}
              dataSource={groupsByRegion(region.id)}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No groups in this region yet' }}
            />
          ),
        }}
      />

      <Modal
        title={regionModal?.type === 'edit' ? 'Edit region' : 'Add new region'}
        open={!!regionModal}
        onOk={submitRegion}
        onCancel={() => setRegionModal(null)}
        okText="Save"
        destroyOnClose
      >
        <Form form={regionForm} layout="vertical">
          <Form.Item name="name" label="Region name" rules={[formRules.required]}>
            <Input placeholder="Region name" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={groupModal?.type === 'edit' ? 'Edit group' : 'Add new group'}
        open={!!groupModal}
        onOk={submitGroup}
        onCancel={() => setGroupModal(null)}
        okText="Save"
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item name="name" label="Group name" rules={[formRules.required]}>
            <Input placeholder="Group name" />
          </Form.Item>
        </Form>
      </Modal>
    </CommonLayout>
  );
};
